import {noop} from '@benchling/js-utils';
import React, {forwardRef} from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants, {DNA_OLIGO_SEARCH_TYPE} from 'constants/ModelTypeConstants';
import {SelectGridMode} from 'constants/SampleConstants';
import {
  SequenceSummary,
  SequenceTopology,
  SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT,
} from 'constants/SequenceConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import getConfig from 'getConfig';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import SampleRestrictionStatusTypedValueSelector from 'inventory/sample-status/SampleRestrictionStatusTypedValueSelector';
import SampleRolesPartiesSearchInput from 'inventory/SampleRolesPartiesSearchInput';
import {getStorableBarcode} from 'inventory/storableUtil';
import LookupConfigCellDropdown from 'lab-automation/settings/automation-input-file-config/LookupConfigCellDropdown';
import PlateLayoutCellDropdown from 'lab-automation/settings/automation-input-file-config/PlateLayoutCellDropdown';
import EditSequenceBasesModal from 'modbio/structured-tables/EditSequenceBasesModal';
import BulkAssemblyBinSelector from 'molbio/bulk-assembly/tables/BulkAssemblyBinSelector';
import BulkAssemblyConstructFragmentSelector from 'molbio/bulk-assembly/tables/BulkAssemblyConstructFragmentSelector';
import BulkAssemblyTableRestrictionEnzymeSelector from 'molbio/bulk-assembly/tables/BulkAssemblyTableRestrictionEnzymeSelector';
import OligoMetadata from 'molbio/components/OligoMetadata';
import BlobInput from 'notebook/data-tables/BlobInput';
import {tryParse, tryParseDatetimeAsDate} from 'notebook/data-tables/DatetimeFields';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {TabEvent} from 'notebook/mediocre/EditorTypes';
import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import LinkSearchDropdown, {
  LinkSearchDefaultQueryMessage,
} from 'notebook/mediocre/mentions/LinkSearchDropdown';
import trackInternalLink from 'notebook/mediocre/mentions/trackInternalLink';
import {
  editableListDidMount,
  editableListDidUpdate,
  EditableListProps,
  editableListWillMount,
} from 'notebook/mediocre/selection/text/EditableListUtil';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import MultiSelectOptionSearchCellDropdown from 'notebook/plain-tables/selectors/MultiSelectOptionSearchCellDropdown';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import OptionSearchCellDropdown from 'notebook/plain-tables/selectors/OptionSearchCellDropdown';
import {BasesColumnEntityType} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import {
  ColumnDefinition,
  isBooleanValueColumnDefinition,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {parseTopologyFromString} from 'notebook/structured-tables/columns/TopologyColumnParsers';
import {getPredicate} from 'notebook/structured-tables/columns/UnitTypePredicates';
import StructuredTableDatetimeInputContainer from 'notebook/structured-tables/components/StructuredTableDatetimeInputContainer';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {
  LastSyncedUpstreamValue,
  StructuredTableColumnGroups,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import FunctionHelpDropdown from 'notebook/tables/core-ui/FunctionHelpDropdown';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import * as SpreadsheetActions from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  BaseTypedValueType,
  Cell,
  RowMetadata,
  TABLE_MAX_COLUMNS,
  TableColumnType,
  TableIndex,
  TypedValue,
} from 'notebook/tables/TableConstants';
import tableMultiKeyDownHandler from 'notebook/tables/tableMultiKeyDownHandler';
import ArrayTypedValueInput, {
  isPrimitiveMultiFieldType,
} from 'notebook/tables/typed-values/components/editors/ArrayTypedValueInput';
import TagValueTypedValueInput from 'notebook/tables/typed-values/components/editors/TagValueTypedValueInput';
import {
  canParseTypedValueFromString,
  convertTypedValueBlob,
  getBioentitySummaryFromTypedValue,
  getStorableInfoFromTypedValue,
  getTypedValueFromBioentitySummary,
  isTypedValueEmpty,
  sanitizeBenchlingBlobs,
} from 'notebook/tables/typed-values/DataTypes';
import {
  ContainersColumnMode,
  TransferTableColumnGroup,
} from 'notebook/transfer-tables/TransferTableConstants';
import preferences from 'Preferences';
import ProcedureSchemaFieldTypeSelectorForTables from 'procedures/shared-components/schema-field-type-selectors/ProcedureSchemaFieldTypeSelectorForTables';
import {ProcedureConditionReplicateCellEditor} from 'procedures/tables/ProcedureConditionReplicateColumn';
import ProcessTaskAssigneeSearchInput from 'processes/tables/ProcessTaskAssigneeSearchInput';
import ProcessTaskStatusSelector from 'processes/tables/ProcessTaskStatusSelector';
import {toTestDefinitionTypedValueFromId} from 'sample-tests/search/testDefinitionSearchUtils';
import SelectParentGridModal from 'samples/storables/SelectParentGridModal';
import SelectParentStorableModal from 'samples/storables/SelectParentStorableModal';
import {ParentStorableType, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import LocationSelectorInput from 'schemas/tags/LocationSelectorInput';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey, keyToEmptyFilter, Operator, SearchContext} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import {toProcedureSearchItemsForCell} from 'studies/search/ProcedureColumnUtils';
import ProcedureSearchInput from 'studies/search/ProcedureSearchInput';
import {toProcedureTypedValueFromSearchResponse} from 'studies/search/ProcedureTypedValueUtils';
import {
  toStudiesTypedValueFromSearchItems,
  toStudySearchItemsFromTypedValue,
} from 'studies/StudiesTypedValueUtils';
import StudySearchInput from 'studies/StudySearchInput';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import ChipSearchInput, {deprecatedSearchGet} from 'ui-library/inputs/ChipSearchInput';
import DateField from 'ui-library/inputs/DateField';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import ModalAnchor from 'ui-library/ModalAnchor';
import {ACCEPTED_UNIT_CONVERSION} from 'units/AnalyticsConstants';
import QueryingUnitConversionDropdownMenu, {
  isUnitConversionPossible,
  UnitConversionDropdownHandle,
} from 'units/UnitConversionDropdownMenu';
import UnitReferenceCellInput from 'units/UnitReferenceCellInput';
import APIUtil from 'util/APIUtil';
import {isInputElement} from 'util/DOMTypePredicates';
import * as keyboard from 'util/Keyboard';
import {assertNever, ElementClipboardEvent, ElementKeyboardEvent, Optional} from 'util/Types';

import {StorableSummaryGraphQL} from '../../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import excess from '../excess';
import FormulaUtil, {
  CELL_INSERT_PLACEHOLDER_CLASS,
  CellInsertLoc,
  CellInsertMode,
} from '../excess/FormulaUtil';
import {SpreadsheetFunction} from '../excess/FunctionConstants';
import {
  BlobLinkSchemaFieldTypedValue,
  LookupConfigInfoTypedValue,
  PlateLayoutTypedValue,
  StorableInfo,
  TestDefinitionTypedValue,
} from '../typed-values/TypedValueTypes';
import FunctionSearchDropdown from './FunctionSearchDropdown';

const SAW_FORMULA_INSERT_CELL_HELP_PREFERENCE_KEY = 'notebook.saw_formula_insert_cell_help';

let tableUsageTracked = false;

const SEARCH_PREFIX = '@';

interface EnhancedProps {
  formula: string;
  formulaHTML: string;
  typedValue: TypedValue | null;
  /**
   * Fallback value used to cascade through null values starting with
   * the store typed value -> cell typed value -> evaluated cell value
   */
  fallbackTypedValue: TypedValue | null;
  editingTable: string | null;
  editingDocumentName: string | null;
  selection: BenchlingSelection | null;
  cellInsertLoc: CellInsertLoc;
}

function FormulaEditableWrapper(props: FormulaEditableProps): JSX.Element {
  const spreadsheetStoreState = useStoreSelector(
    SpreadsheetStore,
    (store) => store.getState(props.tableNamespace, props.tableName),
    isEqual
  );
  return (
    <FormulaEditable
      {...props}
      formula={spreadsheetStoreState.formula}
      formulaHTML={spreadsheetStoreState.formulaHTML}
      typedValue={spreadsheetStoreState.typedValue}
      fallbackTypedValue={
        spreadsheetStoreState.typedValue ?? props.cell?.typedValue ?? props.evaluatedCell?.value ?? null
      }
      editingTable={spreadsheetStoreState.editingTable}
      editingDocumentName={spreadsheetStoreState.editingDocumentName}
      selection={spreadsheetStoreState.selection}
      cellInsertLoc={spreadsheetStoreState.cellInsertLoc}
    />
  );
}

export interface FormulaEditableProps {
  cell?: Cell;
  evaluatedCell?: EvaluatedCell;
  fileId: string;
  tableNamespace: string;
  tableName: string;
  documentName: string | null;
  cid: string;
  isFormulaBox: boolean;
  columnDefinition?: ColumnDefinition | null;
  // Many of the optional props here such as cell and cellTabKeyDown are dependent on whether columnDefinition
  // is specified. Because of this, we have ! scattered across this file after verifiying the presence of
  // columnDefinition. In the future, we will refactor to either specify these in FormulaBox or create a new
  // component that requires these props.
  formulaArrowKeyDown?: (formula: string, e: ElementKeyboardEvent) => void;
  formulaTabKeyDown?: (formula: string, {shiftKey}: TabEvent) => void;
  cellTabKeyDown?: ({shiftKey}: ElementKeyboardEvent) => void;
  escapeHandler: () => void;
  onFormulaFinalize: (e: {
    text: Optional<string>;
    cellData: Partial<Cell<unknown>>;
    enterKey?: boolean | undefined;
    shiftKey?: boolean | undefined;
    index?: TableIndex | undefined;
  }) => void;
  getLastIndex: () => TableIndex;
  onFormulaFocus: (formula: string) => void;
  lastSyncedUpstreamValue?: LastSyncedUpstreamValue;
  // Used by TableCell to indicate if we're being rendered from a StructuredTable.
  // See StructuredTableContext.ts
  structuredTableKind?: StructuredTableKind | null;
  locked?: boolean;
  className?: string;
  index: TableIndex | null;
  keyDownHook?: (e: ElementKeyboardEvent) => boolean | void;
  style?: React.CSSProperties;
  rowMetadata: RowMetadata | null;
  disableFormulas?: boolean;
}

interface FormulaEditableState {
  isAtSearchOpen: boolean;
  functionSearchQuery: string | null;
  /**
   * Current scoped function and the current index within that function, or null if parsing failed.
   */
  functionHelp: {function: SpreadsheetFunction; currentParameterIndex: number} | null;
  isUnitConversionPossible: boolean;
}

export class FormulaEditable extends React.PureComponent<FormulaEditableProps & EnhancedProps> {
  selection: BenchlingSelection | null = null;
  static parseQueryFromFormula = (formula: string): string =>
    formula.startsWith(SEARCH_PREFIX) ? formula.slice(SEARCH_PREFIX.length) : formula;

  dropdownRef = React.createRef<LinkSearchDropdown>();
  functionDropdownRef = React.createRef<AttachDropdown>();
  unitConversionDropdownRef = React.createRef<UnitConversionDropdownHandle>();

  getInitialState = (): FormulaEditableState => ({
    isAtSearchOpen: Boolean(
      // Search is triggered if the cell starts with @...
      this.props.formula.startsWith(SEARCH_PREFIX) &&
        // ... and the FormulaEditable was given focus.
        this.props.selection &&
        this.props.selection.cid === this.props.cid
    ),
    functionSearchQuery: null,
    functionHelp: null,
    isUnitConversionPossible: false,
  });

  override state: FormulaEditableState = this.getInitialState();

  override componentDidUpdate = (
    prevProps: FormulaEditableProps & EnhancedProps,
    prevState: FormulaEditableState
  ): void => {
    if (prevProps.formula !== this.props.formula || prevProps.selection !== this.props.selection) {
      const tokens = excess.tokenizer.partialTokenize(this.props.formula, {relative: false});
      const maybeSchemaField =
        // eslint-disable-next-line benchlint/no-in-operator
        this.props.columnDefinition && 'schemaField' in this.props.columnDefinition
          ? this.props.columnDefinition.schemaField
          : undefined;
      this.setState({
        isAtSearchOpen: Boolean(
          // Search is triggered if there is asigned in user
          EntityStore.currentUser() &&
            // ... and the cell starts with @...
            this.props.formula.startsWith(SEARCH_PREFIX) &&
            // ... and the FormulaEditable was given focus.
            this.props.selection &&
            this.props.selection.cid === this.props.cid
        ),

        // A formula search is open if...
        functionSearchQuery:
          !this.props.disableFormulas &&
          // the user is editing a formula
          this.props.formula.startsWith('=') &&
          // ... and the FormulaEditable was given focus
          this.props.selection &&
          this.props.selection.cid === this.props.cid &&
          // and the cursor is at the end of the formul[1]...
          this.props.selection.start === this.props.selection.end &&
          this.props.formula.length === this.props.selection.end &&
          // ...where the user has started typing a function name
          tokens.leftover &&
          /^(\w+)$/.test(tokens.leftover)
            ? tokens.leftover
            : null,
        // [1] It would be nice if we also detected modifying an existing function name _not_ at the end of
        // formula, but detecting at the end likely covers the majority of use cases.

        // A function help is open if...
        functionHelp:
          !this.props.disableFormulas &&
          // the user is editing a formula
          this.props.formula.startsWith('=') &&
          // ... and the FormulaEditable was given focus
          this.props.selection &&
          this.props.selection.cid === this.props.cid
            ? FormulaUtil.getScopedFunction(this.props.formula, this.props.selection.end)
            : null,

        isUnitConversionPossible: isUnitConversionPossible(this.props.formula, maybeSchemaField),
      });
    }
    editableListDidUpdate(
      ReactDOM.findDOMNode(this) as Element,
      this._getEditableListProps(prevProps),
      this._getEditableListProps(this.props),
      this.selection,
      this._setSelection
    );

    this._maybeShowCellInsertHelp();
  };

  constructor(props: FormulaEditableProps & EnhancedProps) {
    super(props);
    editableListWillMount(this._setSelection);
  }

  override componentDidMount = (): void => {
    editableListDidMount(
      ReactDOM.findDOMNode(this) as Element,
      this._getEditableListProps(this.props),
      this._setSelection
    );
    this._maybeShowCellInsertHelp();
  };

  /**
   * If the user has never seen the popover text that explains you can now do keyboard-based insertion of cell
   * references, automatically show it to help with feature discoverability. Only show it once to avoid
   * annoying the user with same static help text.
   *
   * Should be called on componentDidMount (to catch typing `=` to enter edit mode),
   * and on componentDidUpdate (to catch typing `=` while already in edit mode).
   */
  _maybeShowCellInsertHelp = (): void => {
    if (this.props.disableFormulas) {
      $(ReactDOM.findDOMNode(this) as Element)
        .find(`.${CELL_INSERT_PLACEHOLDER_CLASS}`)
        .tooltip('destroy');
    }

    if (
      !preferences.get(SAW_FORMULA_INSERT_CELL_HELP_PREFERENCE_KEY) &&
      this.props.selection?.cid === this.props.cid &&
      this.props.tableName != null
    ) {
      if (this.props.formulaHTML.includes(CELL_INSERT_PLACEHOLDER_CLASS)) {
        preferences.set(SAW_FORMULA_INSERT_CELL_HELP_PREFERENCE_KEY, true);
        $(ReactDOM.findDOMNode(this) as Element)
          .find(`.${CELL_INSERT_PLACEHOLDER_CLASS}`)
          .tooltip('show');
      }
    }
  };

  _extendCellRange = (e: ElementKeyboardEvent): boolean => {
    if (!this.props.cellInsertLoc) {
      return false;
    }
    e.preventDefault();
    const dir = keyboard.getArrowKeyDirection(e);
    const targetReplaceCell = this.props.cellInsertLoc.target ?? {
      table: this.props.tableName,
      document: this.props.documentName,
      row: this.props.index![0],
      column: this.props.index![1],
    };
    const targetCellInEdtingTable =
      targetReplaceCell.table === this.props.editingTable &&
      targetReplaceCell.document === this.props.editingDocumentName;

    const lastIndex: TableIndex =
      targetReplaceCell.table == null || targetCellInEdtingTable
        ? this.props.getLastIndex()
        : // TODO: If the user is manipulating a table formula in another table (e.g. Table2!A1), we don't
          // actually know its dimensions, so we non-ideally allow unbounded movement on that table. This
          // underscores the fact that our table movement logic is too coupled to the rendered TableEditables.
          // For now, we special case handling of when cell references are manipulated that correspond to the
          // current table since the bounds are more easily threaded through the React component hierarchies
          // (TableEditable -> FormulaBox -> this and TableEditable -> TableRow -> TableCell -> this).
          [getMaxRowsConfig(true) - 1, TABLE_MAX_COLUMNS - 1];
    const [i, j] = FormulaUtil.moveCell({
      index: [targetReplaceCell.row, targetReplaceCell.column],
      lastIndex,
      dir,
    });
    const maybeInsert = FormulaUtil.tryInsert(
      {
        tableName: targetReplaceCell.table ?? this.props.tableName,
        documentName: targetReplaceCell.document ?? this.props.documentName,
      },
      {editingTable: this.props.editingTable, editingDocumentName: this.props.editingDocumentName},
      this.props.formula,
      {
        index: [i, j],
        ...this.props.selection!,
        shift: e.shiftKey,
      }
    );
    if (maybeInsert) {
      SpreadsheetActions.setFormula({
        tableNamespace: this.props.tableNamespace,
        tableName: this.props.tableName,
        index: this.props.index,
        documentName: this.props.documentName,
        fileId: this.props.fileId,
        formula: maybeInsert.formula,
        typedValue: null,
        editing: true,
        selection: maybeInsert.selection,
        cellInsertMode: CellInsertMode.ENABLE,
      });
    }
    return true;
  };

  _getEditableListProps = (
    props: FormulaEditableProps & EnhancedProps
  ): {
    metadataForRestore: {
      selection: BenchlingSelection | null;
    };
  } => ({metadataForRestore: {selection: props.selection}});

  _setSelection = (selection: BenchlingSelection | null): void => {
    this.selection = selection;
  };

  _getTargetSelection = (props: EditableListProps): BenchlingSelection | null =>
    // for EditableListMixin
    props.metadataForRestore.selection;

  _isAnySearchOpen = (): boolean =>
    this.state.isAtSearchOpen ||
    this.state.functionSearchQuery != null ||
    this.state.functionHelp != null ||
    this.state.isUnitConversionPossible;

  _handleArrowKeyDown = (e: ElementKeyboardEvent): void => {
    if (this.state.isAtSearchOpen && this.dropdownRef.current!.queryKeydownHandler(e)) {
      return;
    }
    if (this.state.functionSearchQuery && this.functionDropdownRef.current!.queryKeydownHandler(e)) {
      return;
    }
    if (this.state.isUnitConversionPossible && this.unitConversionDropdownRef.current!.handleKeydown(e)) {
      return;
    }
    if (this._extendCellRange(e)) {
      return;
    }
    if (this.props.formulaArrowKeyDown) {
      this.props.formulaArrowKeyDown(this.props.formula, e);
    }
  };

  _handleTabKeyDown = (e: TabEvent): void => {
    if (this.props.formulaTabKeyDown) {
      this.props.formulaTabKeyDown(this.props.formula, e);
    }
  };

  _keyDownHook = (e: ElementKeyboardEvent): boolean => {
    if (e.ctrlKey) {
      SpreadsheetActions.setFormula({
        tableNamespace: this.props.tableNamespace,
        tableName: this.props.tableName,
        documentName: this.props.documentName,
        index: this.props.index,
        fileId: this.props.fileId,
        formula: this.props.formula,
        typedValue: this.props.typedValue,
        editing: true,
        selection: selutil.getSelectionIndices(),
        cellInsertMode: this.props.cellInsertLoc == null ? CellInsertMode.ENABLE : CellInsertMode.DISABLE,
      });
    }
    return this.props.keyDownHook?.(e) ?? false;
  };

  _handleEnterKeyDown = (e: {
    before: string;
    after: string;
    afterPoint: boolean;
    selected: string;
    modifierKey: boolean;
    shiftKey: boolean;
    originalEvent: ElementKeyboardEvent;
  }): void => {
    if (this.state.isAtSearchOpen && this.dropdownRef.current!.queryKeydownHandler(e.originalEvent)) {
      return;
    }
    if (
      this.state.functionSearchQuery &&
      this.functionDropdownRef.current!.queryKeydownHandler(e.originalEvent)
    ) {
      return;
    }
    if (
      this.state.isUnitConversionPossible &&
      this.unitConversionDropdownRef.current!.handleKeydown(e.originalEvent)
    ) {
      return;
    }
    if (!e.modifierKey) {
      this.setState({isAtSearchOpen: false, functionSearchQuery: null});

      const text = this.props.formula;
      // Only blank out the existing cell data if the text has changed.  This prevents just selecting a TableCell and then hitting <ENTER> from removing links in the cell.
      const cellData = this.props.cell && text === this.props.cell.text ? this.props.cell : {link: undefined};
      this.props.onFormulaFinalize({
        text,
        cellData,
        enterKey: true,
        shiftKey: e.shiftKey,
      });
      return;
    }
    // `modifier + enter` stays in editing mode within the cell and insert a new line.
    const text = `${e.before}\n${e.after}`;
    const pos = e.before.length + 1;
    const selection = {cid: this.props.cid, start: pos, end: pos};
    SpreadsheetActions.setFormula({
      tableNamespace: this.props.tableNamespace,
      tableName: this.props.tableName,
      documentName: this.props.documentName,
      index: this.props.index,
      fileId: this.props.fileId,
      formula: text,
      typedValue: null,
      editing: true,
      selection,
    });
  };

  _handleMouseDown = (e: React.MouseEvent<unknown, MouseEvent>): void => {
    // Prevents the multiWindowMouseDownHandler from firing when dragging inside a cell.
    e.stopPropagation();

    // Prevent triple-click if editable is empty because Firefox sucks and moves the cursor
    // to a weird place...
    if (e.detail > 2 && this.props.formulaHTML.length === 0) {
      e.preventDefault();
    }
  };

  _handleCaretMove = (): void => {
    this._formulaUpdateHandler({text: this.props.formula});
  };

  _formulaUpdateHandler = ({text}: {text: string}): void => {
    const selection = selutil.getSelectionIndices();
    if (text === this.props.formula && isEqual(selection, this.props.selection)) {
      // If nothing has changed, no-op (this frequently occurs as a result of `_handleCaretMove` responding
      // to an explicit call to setFormula). Otherwise, we would need to compute which CellInsertMode to use
      // in the SpreadsheetActions.setFormula call.
      return;
    }
    if (this.props.isFormulaBox && !tableUsageTracked) {
      analytics.track('eln', 'typed_in_formula_box');
      tableUsageTracked = true;
    }
    SpreadsheetActions.setFormula({
      tableNamespace: this.props.tableNamespace,
      tableName: this.props.tableName,
      documentName: this.props.documentName,
      index: this.props.index,
      fileId: this.props.fileId,
      formula: text,
      typedValue: null,
      editing: true,
      selection,
    });
  };

  _formulaPasteHandler = ({
    before,
    after,
    originalEvent,
  }: {
    before: string;
    after: string;
    originalEvent: ElementClipboardEvent;
  }): void => {
    const parts = wyciwyp.processClipboard(originalEvent.clipboardData, {
      parseHTML: false,
    });

    // Determine new text and selection
    const text = before + parts.map((part) => part.text).join(' ') + after;
    const pos = text.length - after.length;
    const selection = {cid: this.props.cid, start: pos, end: pos};

    // Update state. Note: table isn't updated until enter/blur.
    SpreadsheetActions.setFormula({
      tableNamespace: this.props.tableNamespace,
      tableName: this.props.tableName,
      documentName: this.props.documentName,
      index: this.props.index,
      fileId: this.props.fileId,
      formula: text,
      typedValue: null,
      editing: true,
      selection,
    });
  };

  _formulaUndoHandler = (e: ElementKeyboardEvent): boolean => {
    // For now, undo within a cell is disabled.
    if (keyboard.isCtrlZ(e)) {
      e.preventDefault();
      return true;
    }
    return false;
  };

  _formulaFocusHandler = (): void => {
    this.props.onFormulaFocus(this.props.formula);
  };

  _setCellData = (
    text: Optional<string>,
    cellData: {typedValue: TypedValue | null} | {link: string} | {},
    enterKey: boolean = false
  ): void => {
    this.props.onFormulaFinalize({
      text,
      cellData,
      enterKey,
      shiftKey: false,
    });
  };

  /**
   * Stage edits to a cell's typed value in the SpreadsheetStore, but in some case finalize the edit.
   *
   * Finalizing the edit means that the typedValue will be written into the table cell,
   * and any input popover UI will be dismissed.
   *
   * TODO(noters): This should ideally be controlled by the component that calls _handleUpdateTypedValue.
   */
  _handleUpdateTypedValue = (typedValue: TypedValue | null): void => {
    // First, stage the edit in the SpreadsheetStore... and then decide if we should finalize.
    SpreadsheetActions.setTypedValue(typedValue);

    const {columnDefinition} = this.props;
    if (columnDefinition == null) {
      return;
    }

    // If the user cleared the value, there is usually an intent to pick a new value, so don't finalize.
    if (typedValue == null || isTypedValueEmpty(typedValue)) {
      return;
    }

    const schemaField =
      ('schemaField' in columnDefinition ? columnDefinition.schemaField : null) || // eslint-disable-line benchlint/no-in-operator
      ('schemaFieldForFiltering' in columnDefinition ? columnDefinition.schemaFieldForFiltering : null); // eslint-disable-line benchlint/no-in-operator
    const isNonMultiNonDateSchemaField =
      // NB: The function is used for editing, not rendering so its okay to access live schemaField
      schemaField &&
      !schemaField.isMulti &&
      ![SchemaFieldConstants.FIELD_TYPES.DATETIME.type, SchemaFieldConstants.FIELD_TYPES.DATE.type].includes(
        schemaField.fieldType
      );

    if (
      // When selecting a value for a single-select field, we finalize the edit so the user doesn't have to
      // press enter twice. Date(time)s are an exception: users often click around to pick the right value.
      isNonMultiNonDateSchemaField ||
      // The studies and procedures columns will typically contain a single value, so optimize with a similar
      // UX as a single-select field. The user can still re-open the dropdown to add more values if needed.
      columnDefinition.type === TableColumnType.STUDIES ||
      columnDefinition.type === TableColumnType.PROCEDURE ||
      // Test definition is single select, but doesn't have a schema field to be caught above
      columnDefinition.type === TableColumnType.TEST_DEFINITION
    ) {
      this._setCellData('', {typedValue});
    }
  };

  _handleSearchInputKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEscapeKey(e)) {
      this.props.escapeHandler();
      e.preventDefault();
    } else if (keyboard.isTabKey(e)) {
      e.preventDefault();
      this.props.onFormulaFinalize({
        text: this.props.formula,
        cellData: {},
      });
      this.props.cellTabKeyDown!(e);
    } else if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      this.props.onFormulaFinalize({
        text: this.props.formula,
        cellData: {},
        enterKey: true,
      });
    }
  };

  // Keep in sync with NoteEditor.insertInternalLink
  // so that at-mentions behave similarly in tables and paragraphs
  _selectAttachment = (
    itemId: string | number,
    linkedItem: LinkedItem,
    internalLink: MediocreConstants.InternalLink | undefined
  ): void => {
    assert(internalLink !== undefined, `internalLink was undefined for item id: ${itemId}`);
    assert(typeof itemId === 'string', 'Unexpectedly inserted an item with numeric id');
    const url = internalLink.url;
    if (url) {
      this._setCellData(internalLink.name, {link: benchlingUtil.pathToURL(url)});
      if (APIUtil.isAnyEntryOrTemplate(this.props.fileId)) {
        trackInternalLink(
          this.props.fileId,
          itemId,
          linkedItem,
          ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE
        );
      }
    } else {
      // Don't insert a link if there's no URL
      this._setCellData(internalLink.name, {});
    }

    this.setState({isAtSearchOpen: false});
  };

  _handleSelectBlobs = ({
    text,
    blobs,
    filteredExistingBlobs,
  }: {
    text: string;
    blobs: Array<BenchlingBlob>;
    filteredExistingBlobs: Array<BenchlingBlob>;
  }): void => {
    // The function is used for editing, not rendering so its ok to access live schemaField
    const columnDefinition = this.props.columnDefinition;
    const schemaField =
      columnDefinition != null && columnDefinition.hasOwnProperty('schemaField')
        ? (columnDefinition as SchemaFieldColumnDefinition).schemaField
        : null;
    assert(
      schemaField != null && schemaField.fieldType === SchemaFieldConstants.FIELD_TYPES.BLOB_LINK.type,
      'Column must be a blob link field'
    );

    const targetBlobs = schemaField.isMulti ? [...filteredExistingBlobs, ...blobs] : blobs;

    this._setCellData('', {
      typedValue: {
        type: SchemaFieldConstants.FieldType.BLOB_LINK,
        value: text,
        targetBlobs: sanitizeBenchlingBlobs(targetBlobs),
      },
    });
  };

  _handleClosePopup = (): void => {
    this.setState({
      isAtSearchOpen: false,
      functionSearchQuery: null,
      functionHelp: null,
      isUnitConversionPossible: false,
    });
  };

  _completeFunctionName(functionName: string | null): void {
    if (!functionName) {
      this._handleClosePopup();
    } else {
      const newFormula = FormulaUtil.completeFunctionName(this.props.formula, functionName);
      SpreadsheetActions.setFormula({
        tableNamespace: this.props.tableNamespace,
        tableName: this.props.tableName,
        documentName: this.props.documentName,
        index: this.props.index,
        fileId: this.props.fileId,
        formula: newFormula,
        typedValue: null,
        editing: true,
        selection: {
          cid: this.props.cid,
          start: newFormula.length,
          end: newFormula.length,
        },
      });
    }
  }

  _dateInputField = (val: string | null | undefined): React.ReactElement => (
    <DateField
      autoFocus={true}
      labelStyle={DateField.LABEL_STYLES.HIDDEN}
      value={val}
      onKeyDown={(e) => {
        tableMultiKeyDownHandler({
          event: e,
          value: this.props.typedValue,
          onSetValue: (value, enterKey) => {
            this._setCellData('', {typedValue: value}, enterKey);
          },
          onEscape: this.props.escapeHandler,
          onTabKey: this.props.cellTabKeyDown,
        });
      }}
      onChange={(value: string) => {
        this._handleUpdateTypedValue({
          type: BaseTypedValueType.DATE,
          value: tryParseDatetimeAsDate(value) ?? value,
        });
      }}
    />
  );

  _renderMenu = (): JSX.Element | null => {
    if (this.state.isAtSearchOpen) {
      const query = FormulaEditable.parseQueryFromFormula(this.props.formula);
      return (
        <LinkSearchDropdown
          ref={this.dropdownRef}
          query={query}
          selectAttachment={(id, name, url, linkedItem, internalLink) => {
            this._selectAttachment(id, linkedItem, internalLink);
          }}
          fileId={this.props.fileId}
          shouldSearchEntities={true}
          shouldSearchStorables={true}
          width={200}
          emptyQueryMessage={<LinkSearchDefaultQueryMessage />}
        />
      );
    } else if (this.state.functionSearchQuery) {
      return (
        <FunctionSearchDropdown
          ref={this.functionDropdownRef}
          cid={this.props.cid}
          query={this.state.functionSearchQuery}
          onSelect={(functionName) => {
            this._completeFunctionName(functionName);
          }}
        />
      );
    } else if (this.state.functionHelp) {
      return (
        <FunctionHelpDropdown
          spreadsheetFunction={this.state.functionHelp.function}
          currentParameterIndex={this.state.functionHelp.currentParameterIndex}
        />
      );
    } else if (this.state.isUnitConversionPossible) {
      const UnitConversionMenuWithForwardRef = forwardRef(QueryingUnitConversionDropdownMenu);
      const schemaField = (this.props.columnDefinition! as SchemaFieldColumnDefinition).schemaField;
      return (
        <UnitConversionMenuWithForwardRef
          ref={this.unitConversionDropdownRef}
          input={this.props.formula}
          unitId={schemaField.unitApiIdentifier!}
          onSelect={(text) => {
            this._formulaUpdateHandler({text});
            this.props.onFormulaFinalize({
              text,
              cellData: {},
              enterKey: true,
              shiftKey: false,
            });

            analytics.trackEvent(ACCEPTED_UNIT_CONVERSION, {
              action_origin: ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE,
            });
          }}
          integerOnly={schemaField.fieldType === SchemaFieldConstants.FieldType.INTEGER}
        />
      );
    }
    return null;
  };

  _handleChangeBioentity = (bioentitySummaries: Array<BioentitySummary>): void => {
    const bioentitySummary = bioentitySummaries.length ? bioentitySummaries[0] : null;
    this._setCellData('', {
      typedValue: getTypedValueFromBioentitySummary({
        id: bioentitySummary?.id,
        displayLabel: bioentitySummary?.displayLabel,
      }),
    });
  };

  _handleChangeBioentities = (bioentitySummaries: Array<BioentitySummary>): void => {
    this._setCellData('', {
      typedValue: {
        type: BaseTypedValueType.ARRAY,
        rawValue: bioentitySummaries.map((bioentitySummary) =>
          getTypedValueFromBioentitySummary({
            id: bioentitySummary.id,
            displayLabel: bioentitySummary.displayLabel,
          })
        ),
      },
    });
  };

  _handleLocationSelectorChange = (value: string, typedValue: TypedValue): void => {
    if (!typedValue) {
      this._setCellData(value, {typedValue: null});
    } else {
      this._setCellData('', {typedValue});
    }
  };

  _handleChangeParentStorable = (
    parentStorableSummary: StorableInfo | StorableSummaryGraphQL | null
  ): void => {
    const typedValue: TypedValue = parentStorableSummary
      ? {
          type: BaseTypedValueType.STORABLES,
          storableSummaries: [
            {
              id: parentStorableSummary.id,
              name: parentStorableSummary.name,
              barcode: getStorableBarcode(parentStorableSummary),
            },
          ],
        }
      : {type: BaseTypedValueType.STRING, rawValue: ''};
    this._setCellData('', {typedValue});
  };

  _handleChangeSequenceBases = (bases: string): void => {
    this._setCellData('', {
      typedValue: {
        type: BaseTypedValueType.STRING,
        rawValue: bases,
      },
    });
  };

  /**
   * Get selection to request if switching from tag-value mode to formula mode.
   *
   * FormulaEditable takes a hybrid approach to entering values, where it renders a custom component for
   * rich input if the cell has a data type constraint, but will switch back to our regular Excel formula
   * input if the query starts with '='. In most cases, that component renders an <INPUT>. When a user
   * switches between these two modes, we need to manually set the selection since they are two entirely
   * separate DOM nodes.
   *
   * @param {string} newQuery - the new text set via the TagValueTypedValueInput
   * @return {BenchlingSelection}
   */
  _getSelectionForColumnDefinitionInput = (newQuery: string): BenchlingSelection => {
    // Assumption: _renderFromColumnDefinition renders an <INPUT> which has an INPUT-specific selection API.
    const input = document.activeElement;
    if (isInputElement(input)) {
      assert(
        input.selectionStart != null && input.selectionEnd != null,
        `Input did not specify either selectionStart or selectionEnd. Selection cid: ${this.props.cid}`
      );
      return {
        start: input.selectionStart,
        end: input.selectionEnd,
        cid: this.props.cid,
      };
    }
    // Reasonable fallback to cursor at end of text in case the assumption was wrong.
    return {start: newQuery.length, end: newQuery.length, cid: this.props.cid};
  };

  _handleUpdateTagValueTypedValueInputQuery = (newQuery: string): void => {
    SpreadsheetActions.setFormula({
      tableNamespace: this.props.tableNamespace,
      tableName: this.props.tableName,
      documentName: this.props.documentName,
      index: this.props.index,
      fileId: this.props.fileId,
      formula: newQuery,
      typedValue: this.props.typedValue,
      editing: true,
      selection: this._getSelectionForColumnDefinitionInput(newQuery),
    });
  };

  _renderFromSchemaFieldColumnDefinition = (
    columnDefinition: SchemaFieldColumnDefinition
  ): JSX.Element | null => {
    // The function is used for editing, not rendering so its ok to access live schemaField
    switch (columnDefinition.schemaField.fieldType) {
      case SchemaFieldConstants.FIELD_TYPES.BLOB_LINK.type: {
        const cellBlobs =
          this.props.cell!.typedValue &&
          (this.props.cell!.typedValue as BlobLinkSchemaFieldTypedValue).targetBlobs
            ? (this.props.cell!.typedValue as BlobLinkSchemaFieldTypedValue).targetBlobs
            : [];
        return (
          <BlobInput
            allowMultiple={Boolean(columnDefinition.schemaField.isMulti)}
            blobs={cellBlobs.map((blob) => convertTypedValueBlob(blob))}
            onBlobsCreated={({text, blobs, filteredExistingBlobs}) => {
              this._handleSelectBlobs({text, blobs, filteredExistingBlobs});
            }}
            escapeHandler={this.props.escapeHandler}
          />
        );
      }
      case SchemaFieldConstants.FIELD_TYPES.SELECTOR.type:
        return (
          <TagValueTypedValueInput
            cell={this.props.cell!}
            initialQuery={this.props.formula}
            query={this.props.formula}
            typedValue={this.props.typedValue}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onSetCellData={(
              text: Optional<string>,
              cellData: {typedValue: TypedValue | null} | {link: string} | {}
            ) => {
              this._setCellData(text, cellData, true);
            }}
            onUpdateTypedValue={this._handleUpdateTypedValue}
            onEscape={this.props.escapeHandler}
            schemaField={columnDefinition.schemaField}
            onTabKey={this.props.cellTabKeyDown}
          />
        );
      case SchemaFieldConstants.FIELD_TYPES.AA_PART_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.BATCH_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.BIOENTITY_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.DATE.type:
      case SchemaFieldConstants.FIELD_TYPES.ENTRY_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.PART_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.STORABLE_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.TRANSCRIPTION_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.TRANSLATION_LINK.type:
      case SchemaFieldConstants.FIELD_TYPES.DATETIME.type:
      case SchemaFieldConstants.FIELD_TYPES.BOOLEAN.type:
        return (
          <TagValueTypedValueInput
            cell={this.props.cell!}
            initialQuery={this.props.formula}
            query={this.props.formula}
            typedValue={
              this.props.typedValue ?? this.props.cell?.typedValue ?? this.props.evaluatedCell?.value ?? null
            }
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onSetCellData={(
              text: Optional<string>,
              cellData: {typedValue: TypedValue | null} | {link: string} | {},
              enterKey?: boolean
            ) => {
              this._setCellData(
                text,
                cellData,
                getConfig('ELN_ENABLE_TABLE_ALL_CELLS_NAV_DOWN_ON_ENTER') && enterKey
              );
            }}
            onUpdateTypedValue={this._handleUpdateTypedValue}
            onEscape={this.props.escapeHandler}
            schemaField={columnDefinition.schemaField}
            onTabKey={this.props.cellTabKeyDown}
          />
        );
      case SchemaFieldConstants.FIELD_TYPES.FLOAT.type:
      case SchemaFieldConstants.FIELD_TYPES.INTEGER.type:
      case SchemaFieldConstants.FIELD_TYPES.TEXT.type:
      case SchemaFieldConstants.FIELD_TYPES.LONG_TEXT.type:
      case SchemaFieldConstants.FIELD_TYPES.JSON.type: {
        const fieldType = columnDefinition.schemaField.fieldType;
        assert(
          canParseTypedValueFromString(fieldType),
          `Tried to use a string input for a type that can't be parsed from a string: ${fieldType}`
        );
        // TODO(BNCH-101106): Remove the following condition once we have a better way to handle array-typed columns
        // Show array cell editor if the schema field is a multi primitive field. This should only be used for non-persisted parameter tables.
        if (columnDefinition.schemaField.isMulti && isPrimitiveMultiFieldType(fieldType)) {
          return (
            <ArrayTypedValueInput
              subtype={fieldType}
              schemaField={columnDefinition.schemaField}
              query={this.props.formula}
              typedValue={
                this.props.typedValue ??
                this.props.cell?.typedValue ??
                this.props.evaluatedCell?.value ??
                null
              }
              onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
              onSetCellData={(
                text: Optional<string>,
                cellData: {typedValue: TypedValue | null} | {link: string} | {},
                enterKey?: boolean
              ) => {
                this._setCellData(
                  text,
                  cellData,
                  getConfig('ELN_ENABLE_TABLE_ALL_CELLS_NAV_DOWN_ON_ENTER') && enterKey
                );
              }}
              onUpdateTypedValue={this._handleUpdateTypedValue}
              onEscape={this.props.escapeHandler}
              onTabKey={this.props.cellTabKeyDown}
            />
          );
        }
        return null;
      }
      default:
        return null;
    }
  };

  /**
   * Return a custom component for editing this cell, or null if we should fall back to the default
   * behavior.
   */
  _renderFromColumnDefinition = (
    columnDefinition: ColumnDefinition,
    structuredTableKind?: StructuredTableKind | null
  ): JSX.Element | null => {
    // We require a non-null structuredTableKind if inStructuredTable, see StructuredTableContext
    const inStructuredTable = structuredTableKind != null;
    const {typedValue, fallbackTypedValue} = this.props;
    if (isBooleanValueColumnDefinition(this.props.columnDefinition!)) {
      return null;
    }
    switch (columnDefinition.type) {
      case TableColumnType.ALIAS:
        return null;
      case TableColumnType.DNA_BASES:
      case TableColumnType.RNA_BASES:
      case TableColumnType.DNA_HELM:
      case TableColumnType.RNA_HELM:
      case TableColumnType.DNA_IDT:
      case TableColumnType.RNA_IDT:
        return null;
      case TableColumnType.BASES:
        if (
          [BasesColumnEntityType.DNA_OLIGO, BasesColumnEntityType.RNA_OLIGO].includes(
            columnDefinition.basesColumnConfig.entityType
          )
        ) {
          return null;
        } else if (typedValue?.type === BaseTypedValueType.STRING || typedValue == null) {
          const initialBasesValue = this.props.cell?.text;
          return (
            <EditSequenceBasesModal
              initialValue={initialBasesValue}
              basesColumnConfig={columnDefinition.basesColumnConfig}
              onRequestSubmit={this._handleChangeSequenceBases}
              cid={this.props.cid}
            />
          );
        } else {
          assertAndContinue(false, 'Expected bases column to be string typed value');
          return null;
        }
      case TableColumnType.SEQUENCE_TOPOLOGY:
        return (
          <OptionCellDropdown
            locked={false}
            isSelected={false}
            options={[
              SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT[SequenceTopology.LINEAR],
              SEQUENCE_TOPOLOGY_TO_DISPLAY_TEXT[SequenceTopology.CIRCULAR],
            ]}
            onUpdate={(newTopology) => {
              this._setCellData('', {
                typedValue: {
                  type: BaseTypedValueType.STRING,
                  rawValue: parseTopologyFromString(newTopology) ?? '',
                },
              });
            }}
            fullWidth={true}
          />
        );
      case TableColumnType.SMILES:
        return null;
      case TableColumnType.BATCH:
        return <span>{this.props.cell!.text}</span>;
      case TableColumnType.BIOENTITY_NAME:
        return null;
      case TableColumnType.BOX: {
        if (
          (structuredTableKind !== StructuredTableKind.TRANSFER &&
            structuredTableKind !== StructuredTableKind.LOOKUP) ||
          columnDefinition.registryId == null
        ) {
          return null;
        }
        return (
          <LocationSelectorInput
            onSetData={this._handleLocationSelectorChange}
            onEscape={this.props.escapeHandler}
            onTabKey={this.props.cellTabKeyDown!}
            onUpdateTypedValue={this._handleUpdateTypedValue}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            query={this.props.formula}
            typedValue={typedValue}
            registryId={columnDefinition.registryId}
            parentStorableType={ParentStorableType.BOX}
            includeArchived={false}
            storablesToBeMoved={
              structuredTableKind === StructuredTableKind.TRANSFER &&
              columnDefinition.columnGroup === TransferTableColumnGroup.TARGET
                ? [{storableSchema: columnDefinition.containerSchema}]
                : null
            }
          />
        );
      }
      case TableColumnType.CONCENTRATION:
        return null;
      case TableColumnType.CONTAINERS: {
        if (
          structuredTableKind !== StructuredTableKind.LOOKUP &&
          (columnDefinition.containersColumnMode === ContainersColumnMode.LEGACY ||
            columnDefinition.containersColumnMode === ContainersColumnMode.NEW_DESTINATION_FOR_TRANSFER_TABLE)
        ) {
          return null;
        }
        const storableInfo = getStorableInfoFromTypedValue(this.props.cell!.typedValue!);
        return (
          <StorableSearchInput
            storableTypes={[ModelTypeConstants.CONTAINER]}
            summarize={false}
            // Control the query based on the formula text to allow for quick input with string resolution
            // to be used
            query={this.props.formula}
            initialQuery={this.props.formula}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onKeyDown={this._handleSearchInputKeyDown}
            onChange={(storables) => {
              this._handleChangeParentStorable(storables[0]);
            }}
            selectedItems={storableInfo ? [storableInfo] : []}
            selectedStorables={storableInfo ? [storableInfo] : []}
            shouldAllowMultiple={false}
            storableSchemaId={columnDefinition.containerSchemaId}
          />
        );
      }
      case TableColumnType.DEDICATED_LOOKUP:
        // Lookup columns should be read-only. But in case one does get edited for some reason,
        // we can fall back to the basic text box.
        // TODO(BNCH-21592): the Lookup Columns customEval should return {locked: true}
        return null;
      case TableColumnType.CUSTOM: {
        const customTypedValue = typedValue ?? this.props.cell!.typedValue;
        const {dataType} = columnDefinition.customColumnConfig;
        switch (dataType) {
          case CustomColumnDataType.TEXT:
          case CustomColumnDataType.INTEGER:
          case CustomColumnDataType.FLOAT:
          case CustomColumnDataType.BOOLEAN:
            return null;
          case CustomColumnDataType.DATE:
          case CustomColumnDataType.DATETIME: {
            const temporalInput =
              dataType === CustomColumnDataType.DATE ? (
                this._dateInputField(
                  customTypedValue?.type === BaseTypedValueType.DATE ? customTypedValue.value : ''
                )
              ) : (
                <DatetimeField
                  labelStyle={DatetimeField.LABEL_STYLES.HIDDEN}
                  value={customTypedValue?.type === BaseTypedValueType.DATETIME ? customTypedValue.value : ''}
                  onChange={(value: string) => {
                    this._handleUpdateTypedValue({
                      type: BaseTypedValueType.DATETIME,
                      value: tryParse(value) ?? value,
                    });
                  }}
                />
              );
            // wrapping in a StructuredTableDatetimeInputContainer helps position the date picker correctly
            return (
              <StructuredTableDatetimeInputContainer>{temporalInput}</StructuredTableDatetimeInputContainer>
            );
          }

          default:
            assertNever(dataType);
            return null;
        }
      }
      case TableColumnType.EXISTING_BIOENTITY: {
        // TODO(BNCH-41594) Handle array typed values
        const bioentitySummary = getBioentitySummaryFromTypedValue(this.props.cell!.typedValue!);
        const tagSchemaOrInterface = columnDefinition.tagSchemaOrInterface;
        // If the tagSchema is 'ALL' we want to search across all schemas so we don't filter to a single id
        const tagSchemaOrInterfaceId = tagSchemaOrInterface === 'ALL' ? null : tagSchemaOrInterface?.id;
        return (
          <BioentitySearchInput
            // Control the query based on the formula text to allow for quick input with string resolution
            // to be used
            query={this.props.formula}
            initialQuery={this.props.formula}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onKeyDown={this._handleSearchInputKeyDown}
            tagSchemaOrInterfaceId={tagSchemaOrInterfaceId}
            onChange={this._handleChangeBioentity}
            selectedItems={bioentitySummary ? [bioentitySummary] : []}
            containableItemsOnly={structuredTableKind === StructuredTableKind.TRANSFER}
            scrollable={true}
          />
        );
      }
      case TableColumnType.COMPLEX_POLYMER_COMPONENT: {
        const typedValue =
          this.props.typedValue ?? this.props.cell?.typedValue ?? this.props.evaluatedCell?.value ?? null;
        const typedValueIsArray = typedValue?.type === BaseTypedValueType.ARRAY;
        const bioentitySummaries = typedValueIsArray
          ? typedValue.rawValue.map((value) => getBioentitySummaryFromTypedValue(value))
          : [];

        const {componentSchemaField} = columnDefinition;
        const targetSchemaId = (
          componentSchemaField?.requiredLink as SchemaFieldConstants.RequiredLinkTagSchema | undefined
        )?.tagSchema?.id;
        const hasTargetSchema =
          componentSchemaField != null &&
          SchemaFieldConstants.isBioentityLinkFieldWithTargetSchema(componentSchemaField) &&
          targetSchemaId != null;

        // This should never happen. But in case we have a misconfiguration somehow, we render a disabled
        //   search input instead of hard asserting and crashing the entry.
        assertAndContinue(hasTargetSchema, 'Expected bioentity link field with required link tag schema');

        return (
          <BioentitySearchInput
            initialQuery={this.props.formula}
            onKeyDown={this._handleSearchInputKeyDown}
            onChange={this._handleChangeBioentities}
            selectedItems={bioentitySummaries.flatMap((maybeSummary) =>
              maybeSummary != null ? [maybeSummary] : []
            )}
            tagSchemaOrInterfaceId={targetSchemaId ?? null}
            shouldAllowMultiple={columnDefinition.allowsMultipleComponents}
          />
        );
      }
      case TableColumnType.COMPLEX_POLYMER_CONNECTIONS: {
        return null;
      }
      case TableColumnType.OLIGO_DUPLEX_OFFSET: {
        return null;
      }
      case TableColumnType.BIOENTITY_SELECTOR: {
        const {schemaFieldForFiltering} = columnDefinition;
        assert(
          schemaFieldForFiltering != null,
          'Unexpected to have a BIOENTITY_SELECTOR column with no filter'
        );
        return (
          <TagValueTypedValueInput
            cell={this.props.cell!}
            initialQuery={this.props.formula}
            query={this.props.formula}
            typedValue={typedValue}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onSetCellData={(
              text: Optional<string>,
              cellData: {typedValue: TypedValue | null} | {link: string} | {}
            ) => {
              this._setCellData(text, cellData, true);
            }}
            onUpdateTypedValue={this._handleUpdateTypedValue}
            onEscape={this.props.escapeHandler}
            schemaField={schemaFieldForFiltering}
            onTabKey={this.props.cellTabKeyDown}
          />
        );
      }
      case TableColumnType.MISSING_FIELD:
        return null;
      case TableColumnType.NUMBER_OF_CONTAINERS:
        return null;

      case TableColumnType.PARENT_STORABLE: {
        // We use the inline location selector for all structured tables (always). For other
        // table types (e.g., transfer table), the inline location selector is behind a flag.
        if (BenchlingConfig.ELN_ENABLE_INLINE_LOCATION_SELECTOR || inStructuredTable) {
          return (
            <LocationSelectorInput
              onSetData={this._handleLocationSelectorChange}
              onEscape={this.props.escapeHandler}
              onTabKey={this.props.cellTabKeyDown!}
              onUpdateTypedValue={this._handleUpdateTypedValue}
              onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
              query={this.props.formula}
              typedValue={typedValue}
              registryId={
                columnDefinition.storableSchema?.registryId ?? columnDefinition.fallbackRegistryId ?? ''
              }
              parentStorableType={columnDefinition.parentStorableType}
              includeArchived={false}
              storablesToBeMoved={[{storableSchema: columnDefinition.storableSchema ?? undefined}]}
            />
          );
        } else if (columnDefinition.parentStorableIsGrid == null) {
          // Fall back to plaintext input and string resolution
          return null;
        } else if (columnDefinition.parentStorableIsGrid) {
          return (
            <div>
              <SelectParentGridModal
                onRequestClose={this.props.escapeHandler}
                onRequestSubmit={({storable}) => {
                  this._handleChangeParentStorable(storable);
                }}
                storableSchema={columnDefinition.storableSchema ?? undefined}
                mode={SelectGridMode.CHOOSE}
              />
            </div>
          );
        } else {
          return (
            <div>
              <ModalAnchor open={true}>
                <SelectParentStorableModal
                  storableSchema={columnDefinition.storableSchema}
                  modalTitleText="Choose location"
                  onRequestClose={this.props.escapeHandler}
                  onSubmit={({storable}) => {
                    this._handleChangeParentStorable(storable);
                  }}
                  parentStorableTypesOverride={[ModelTypeConstants.LOCATION]}
                  submitText="Save"
                />
              </ModalAnchor>
            </div>
          );
        }
      }
      case TableColumnType.PARTIES: {
        return (
          <SampleRolesPartiesSearchInput
            typedValue={typedValue ?? this.props.cell?.typedValue ?? this.props.evaluatedCell?.value ?? null}
            handleUpdateTypedValue={this._handleUpdateTypedValue}
            onEscape={this.props.escapeHandler}
            onSetCellData={this._setCellData}
            onTabKey={this.props.cellTabKeyDown!}
            allowBenchlingApps={columnDefinition.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS}
          />
        );
      }
      case TableColumnType.PLATE: {
        if (
          (structuredTableKind !== StructuredTableKind.TRANSFER &&
            structuredTableKind !== StructuredTableKind.INVENTORY &&
            structuredTableKind !== StructuredTableKind.LOOKUP) ||
          columnDefinition.registryId == null
        ) {
          return null;
        }
        return (
          <LocationSelectorInput
            onSetData={this._handleLocationSelectorChange}
            onEscape={this.props.escapeHandler}
            onTabKey={this.props.cellTabKeyDown!}
            onUpdateTypedValue={this._handleUpdateTypedValue}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            query={this.props.formula}
            typedValue={typedValue}
            registryId={columnDefinition.registryId}
            parentStorableType={
              columnDefinition.plateSchemaType === PlateSchemaType.MATRIX_PLATE
                ? ParentStorableType.MATRIX_PLATE
                : ParentStorableType.PLATE
            }
            includeArchived={false}
            storablesToBeMoved={
              structuredTableKind === StructuredTableKind.TRANSFER &&
              columnDefinition.columnGroup === TransferTableColumnGroup.TARGET &&
              columnDefinition.plateSchemaType === PlateSchemaType.MATRIX_PLATE
                ? [{storableSchema: columnDefinition.containerSchema}]
                : null
            }
            storableSchemaId={columnDefinition.plateSchemaId}
          />
        );
      }
      case TableColumnType.POSITION:
        return null;
      case TableColumnType.TAG_VALUE:
        return this._renderFromSchemaFieldColumnDefinition(columnDefinition);
      case TableColumnType.STAGE_RUN:
        // Just render a normal editor for now.
        return null;
      case TableColumnType.STRING:
        return null;
      case TableColumnType.PROCESS_TASK:
        // TODO(BNCH-21728): A better task cell editor may be needed
        return null;
      case TableColumnType.PROCESS_OUTPUT:
        return null;
      case TableColumnType.PROCESS_TASK_STATUS: {
        return (
          <ProcessTaskStatusSelector
            lastSyncedUpstreamValue={this.props.lastSyncedUpstreamValue?.value}
            typedValue={typedValue}
            statusLifecycle={columnDefinition.statusLifecycle}
            onUpdate={this._handleUpdateTypedValue}
          />
        );
      }
      case TableColumnType.PROCESS_TASK_ASSIGNEE: {
        return (
          <ProcessTaskAssigneeSearchInput
            value={(typedValue || this.props.cell!.typedValue) ?? null}
            onChange={(assignee) => {
              this._handleUpdateTypedValue(assignee);
            }}
            initialQuery={this.props.formula}
          />
        );
      }
      case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE: {
        const processTaskTypedValue = typedValue || this.props.cell!.typedValue;
        return (
          <StructuredTableDatetimeInputContainer>
            {this._dateInputField(
              processTaskTypedValue?.type === BaseTypedValueType.DATE ? processTaskTypedValue.value : ''
            )}
          </StructuredTableDatetimeInputContainer>
        );
      }
      case TableColumnType.PROCEDURE: {
        return (
          <ProcedureSearchInput
            query={this.props.formula}
            procedureTypes={columnDefinition.procedureTypes}
            placeholder={columnDefinition.placeholder}
            selectedItems={toProcedureSearchItemsForCell(
              typedValue,
              this.props.rowMetadata,
              this.props.columnDefinition
            )}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onKeyDown={this._handleSearchInputKeyDown}
            onChange={(newProcedures) => {
              this._handleUpdateTypedValue(toProcedureTypedValueFromSearchResponse(newProcedures));
            }}
            chipSize={ChipSize.TABLE_CELL}
            shouldAllowMultiple={columnDefinition.shouldAllowMultiple ?? false}
          />
        );
      }
      case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
        return (
          <ProcedureSchemaFieldTypeSelectorForTables
            typedValue={typedValue}
            onUpdate={(tv) => {
              this._handleUpdateTypedValue(tv);
            }}
            context={columnDefinition.context}
          />
        );

      case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
        return (
          <ProcedureConditionReplicateCellEditor
            fileId={this.props.fileId}
            onUpdate={(typedValue) => {
              this._handleUpdateTypedValue(typedValue);
            }}
            typedValue={
              typedValue?.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES ? typedValue : null
            }
          />
        );

      case TableColumnType.SAMPLE_GROUP_SAMPLE:
        return null;
      case TableColumnType.SAMPLE_RESTRICTION_STATUS:
        return (
          <SampleRestrictionStatusTypedValueSelector
            locked={false}
            typedValue={typedValue ?? this.props.cell?.typedValue ?? this.props.evaluatedCell?.value ?? null}
            onUpdate={this._handleUpdateTypedValue}
          />
        );
      case TableColumnType.MEASUREMENT_AMOUNT:
        return null;
      case TableColumnType.OPTION_SELECTOR:
        return (
          <OptionCellDropdown
            locked={false}
            isSelected={false}
            options={columnDefinition.options}
            onUpdate={(newOption) => {
              this._setCellData('', {
                typedValue: {type: BaseTypedValueType.STRING, rawValue: newOption},
              });
            }}
            fullWidth={true}
          />
        );
      case TableColumnType.OPTION_SEARCH_SELECTOR:
        if (columnDefinition.isMulti) {
          return (
            <MultiSelectOptionSearchCellDropdown
              isSelected={false}
              options={columnDefinition.options}
              selectedOptions={[]}
              onUpdate={(newSelectedOptions: Array<string>) => {
                this._setCellData(newSelectedOptions.join(', '), {
                  typedValue: {
                    type: BaseTypedValueType.ARRAY,
                    rawValue: newSelectedOptions.map((option) => ({
                      type: BaseTypedValueType.STRING,
                      rawValue: option,
                    })),
                  },
                });
              }}
            />
          );
        }
        return (
          <OptionSearchCellDropdown
            isSelected={false}
            options={columnDefinition.options}
            onUpdate={(newOption) => {
              this._setCellData(newOption, {
                typedValue: {type: BaseTypedValueType.STRING, rawValue: newOption},
              });
            }}
            fullWidth={true}
          />
        );
      case TableColumnType.UNIT_SELECTOR:
        return (
          <OptionCellDropdown
            locked={false}
            isSelected={false}
            options={columnDefinition.acceptedUnits}
            onUpdate={(newUnits) => {
              this._setCellData('', {
                typedValue: {type: BaseTypedValueType.STRING, rawValue: newUnits},
              });
            }}
            fullWidth={true}
          />
        );
      case TableColumnType.UNIT_REFERENCE:
        return (
          <UnitReferenceCellInput
            query={this.props.formula}
            typedValue={typedValue}
            onKeyDown={this._handleSearchInputKeyDown}
            onQueryChange={(e) => {
              this._formulaUpdateHandler({text: e.target.value});
            }}
            updateTypedValue={(typedValue) => {
              this._setCellData('', {typedValue});
            }}
            unitTypesFilterPredicate={
              columnDefinition.unitTypesFilterPredicateType &&
              getPredicate(columnDefinition.unitTypesFilterPredicateType)
            }
          />
        );
      case TableColumnType.LOOKUP_CONFIG:
        return (
          <LookupConfigCellDropdown
            typedValue={typedValue as LookupConfigInfoTypedValue}
            locked={false}
            isSelected={false}
            onOpenDropdown={noop}
            onUpdate={this._handleUpdateTypedValue}
            fullWidth={true}
          />
        );
      case TableColumnType.PLATE_LAYOUT_SELECTOR:
        return (
          <PlateLayoutCellDropdown
            typedValue={typedValue as PlateLayoutTypedValue}
            onOpenDropdown={noop}
            onUpdate={this._handleUpdateTypedValue}
            isSelected={false}
          />
        );
      case TableColumnType.QUANTITY:
        return null;
      case TableColumnType.INGREDIENT_CATALOG_ID:
      case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
      case TableColumnType.INGREDIENT_NOTES:
        return null;

      case TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER: {
        const bioentitySummary = getBioentitySummaryFromTypedValue(this.props.cell!.typedValue!);
        return (
          <BioentitySearchInput
            initialQuery={this.props.formula}
            onKeyDown={this._handleSearchInputKeyDown}
            bioentityTypes={columnDefinition.bioentityTypeFilters}
            onChange={this._handleChangeBioentity}
            selectedItems={bioentitySummary ? [bioentitySummary] : []}
          />
        );
      }

      case TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER: {
        const bioentitySummary = getBioentitySummaryFromTypedValue(this.props.cell!.typedValue!);
        return (
          <ChipSearchInput
            isMulti={false}
            disableTyping={bioentitySummary != null}
            searchURL={`/1/api/all/search?filter=${DNA_OLIGO_SEARCH_TYPE}`}
            doSearch={deprecatedSearchGet}
            autoFocus={false}
            searchOnEmptyQuery={false}
            placeholder=""
            fetchParams={{context: SearchContext.LISTING}}
            getOptionDatasFromResponse={(response) =>
              response.data.map((primer: SequenceSummary) => ({
                name: (
                  <OligoMetadata
                    name={primer.name}
                    folderName={primer.folder.name}
                    bases={primer.bases!}
                    nucleotideType={primer.nucleotideType}
                  />
                ),
                value: primer,
              }))
            }
            onSelect={(bioentitySummary: BioentitySummary) => {
              this._handleChangeBioentity([bioentitySummary]);
            }}
            value={bioentitySummary ? [bioentitySummary] : []}
            query={this.props.formula}
            onQueryChange={(e) => {
              this._formulaUpdateHandler({text: e.target.value});
            }}
            onKeyDown={this._handleSearchInputKeyDown}
          />
        );
      }

      case TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR: {
        return (
          <BulkAssemblyBinSelector
            typedValue={typedValue}
            onUpdate={this._handleUpdateTypedValue}
            bins={columnDefinition.bins}
            locked={this.props.locked ?? false}
          />
        );
      }

      case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR: {
        return (
          <BulkAssemblyConstructFragmentSelector
            typedValue={typedValue}
            onUpdate={this._handleUpdateTypedValue}
            fragmentDatas={columnDefinition.fragmentDatas ?? []}
            locked={this.props.locked ?? false}
            binColor={columnDefinition.binColor}
            method={columnDefinition.method}
            assemblyId={columnDefinition.assemblyId}
          />
        );
      }
      case TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME: {
        return (
          <BulkAssemblyTableRestrictionEnzymeSelector
            isDisabled={this.props.locked ?? false}
            typedValue={typedValue}
            onUpdate={this._handleUpdateTypedValue}
          />
        );
      }

      case TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT:
      case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME:
      case TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS:
      case TableColumnType.BULK_ASSEMBLY_PRIMER:
      case TableColumnType.BULK_ASSEMBLY_PRIMER_BASES:
      case TableColumnType.BULK_ASSEMBLY_LENGTH:
        return null;

      case TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT:
      case TableColumnType.PLATE_MAP_MATERIAL_INPUT:
        return null;

      case TableColumnType.STUDIES: {
        return (
          <StudySearchInput
            query={this.props.formula}
            selectedItems={toStudySearchItemsFromTypedValue(fallbackTypedValue)}
            onUpdateQuery={this._handleUpdateTagValueTypedValueInputQuery}
            onKeyDown={this._handleSearchInputKeyDown}
            onChange={(newStudies) => {
              this._handleUpdateTypedValue(toStudiesTypedValueFromSearchItems(newStudies));
            }}
            chipSize={ChipSize.TABLE_CELL}
            shouldAllowMultiple={true}
          />
        );
      }

      case TableColumnType.TEST_DEFINITION: {
        const isNotArchivedFilter: Filter = {
          key: FilterKey.ARCHIVE_PURPOSES,
          operator: Operator.IS_ONE_OF,
          value: [NOT_ARCHIVED_PURPOSE_STRING],
        };
        const organizationFilter: Array<Filter> =
          columnDefinition.targetOrganization != null
            ? [
                {
                  key: FilterKey.ORGANIZATION,
                  operator: Operator.IS,
                  value: columnDefinition.targetOrganization.id,
                },
              ]
            : [];

        // TODO(BNCH-105825): Allow test definition typed value cells to contain invalid text and formulas
        return (
          <SearchInput
            autoFocus={false}
            additionalFilters={[isNotArchivedFilter, ...organizationFilter]}
            disableTyping={Boolean(typedValue) || Boolean(this.props.locked)}
            isMulti={false}
            placeholder="Search for a test definition"
            onRemove={() => {
              this._handleUpdateTypedValue(null);
            }}
            onSelect={async (itemId: string) => {
              // TODO(BNCH-105824 & BNCH-105825): Fix the possible race condition caused by asynchronous resolution
              // https://github.com/benchling/aurelia/pull/140034#discussion_r1757767220
              this._handleUpdateTypedValue(await toTestDefinitionTypedValueFromId(itemId));
            }}
            onKeyDown={this._handleSearchInputKeyDown}
            selected={typedValue ? [(typedValue as TestDefinitionTypedValue).id] : []}
            types={[ModelTypeConstants.TEST_DEFINITION]}
          />
        );
      }

      case TableColumnType.ANTIBODY_DOMAIN: {
        // TODO(BNCH-128762) Use the specified schema from the frontend metadata to limit to that schema.
        return (
          <BioentitySearchInput
            initialQuery={this.props.formula}
            bioentityTypes={[ModelTypeConstants.PROTEIN]}
            onKeyDown={this._handleSearchInputKeyDown}
            onChange={this._handleChangeBioentity}
            // Since only one domain can be selected, we don't need to show the selected items in the editor.
            selectedItems={[]}
            shouldAllowMultiple={false}
            additionalFilters={[
              {
                ...keyToEmptyFilter(FilterKey.PROTEIN_AMINO_ACIDS),
                operator: Operator.IS_NOT_EMPTY,
              },
            ]}
          />
        );
      }
      default:
        return null;
    }
  };

  _renderCustomComponent = (
    columnDefinition?: ColumnDefinition | null,
    structuredTableKind?: StructuredTableKind | null
  ): JSX.Element | null => {
    if (ExcessUtil.getFormulaText(this.props.formula) == null && columnDefinition != null) {
      const customComponent = this._renderFromColumnDefinition(columnDefinition, structuredTableKind);
      if (customComponent) {
        // Wrap the component in a div with a data-cid so that the cursor can be placed correctly
        // by editable-list-mixin.
        return (
          <div data-cid={this.props.cid} className="u-height-full">
            {customComponent}
          </div>
        );
      }
    }
    return null;
  };

  override render(): JSX.Element {
    return (
      this._renderCustomComponent(this.props.columnDefinition, this.props.structuredTableKind) || (
        <AbsoluteDropdownContainer
          alwaysDropDown={this.state.isUnitConversionPossible}
          canDropUnder={true}
          open={this._isAnySearchOpen()}
          onRequestClose={this._handleClosePopup}
          menu={this._renderMenu()}
          // Ensure that users can interact with the table cells even if we are overlaying a menu.
          noBackdrop={true}
        >
          <EditableComponent
            {...this.props}
            focusHandler={this._formulaFocusHandler}
            text={this.props.formulaHTML}
            allowHTML={true}
            allowMultiline={true}
            arrowKeydown={this._handleArrowKeyDown}
            tabHandler={this._handleTabKeyDown}
            splitHandler={this._handleEnterKeyDown}
            mouseDownHandler={this._handleMouseDown}
            updateHandler={this._formulaUpdateHandler}
            pasteHandler={this._formulaPasteHandler}
            selectHandler={this._handleCaretMove}
            restoreState={this._formulaUndoHandler}
            formats={null}
            keyDownHook={this._keyDownHook}
            typingPerfConfig={{
              metricName: this.props.isFormulaBox ? 'formula_bar_typing_input' : 'table_cell_typing_input',
            }}
          />
        </AbsoluteDropdownContainer>
      )
    );
  }
}

export default React.memo(FormulaEditableWrapper, (prevProps, nextProps) => isEqual(prevProps, nextProps));
